import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Paper,
  Stack,
  Avatar,
  AppBar,
  Button,
  Modal,
  Tooltip,
  Grid,
} from "@mui/material";
import Chart from "react-apexcharts";
import indianNumberFormatter from "../../utils/indianNumberFormatter";
import { useAuth } from "../../context/authContext";
import { indigo, red, green } from "@mui/material/colors";
import { Helmet } from "react-helmet";
import DashboardStateCard from "./DashboardStateCard/DashboardStateCard";
import { CalendarMonth, ClearOutlined } from "@mui/icons-material";
import { addDays, eachDayOfInterval } from "date-fns";
import {
  calanderDateConverter,
  dateConverter,
} from "../../utils/customDateFunction";
import { DateRangePicker } from "react-date-range";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getCampGraphResult, getSiteId, getCronLogStats } from "../../redux/features/clicksSlice";
import { getCollectionStatsWarningsApi, getCollectionStatsHistoryApi } from "../../redux/api/clicksApi";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent, IconButton } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import moment from "moment";

const cardStyle = {
  borderRadius: 3,
  bgcolor: "#f9fafb",
  border: "1px solid #e5e7eb",
  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  height: "100%",
  display: "flex",
  flexDirection: "column",
};

const headerStyle = {
  px: 2.5,
  py: 1.2,
  fontSize: "1rem",
  fontWeight: 600,
  color: "#fff",
};

const dividerStyle = {
  height: "1px",
  background: "#e5e7eb",
  mx: 2.5,
};

const bodyStyle = {
  p: 2.5,
  display: "flex",
  flexDirection: "column",
  gap: 1.5,
};

const rowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  fontSize: "0.9rem",
  color: "#374151",
};


function Dashboard() {
  const { campGraphResult, siteIdData, cronLogStats } = useSelector((state) => state.clicks);
  const [warnings, setWarnings] = useState([]);
  
  // History Modal States
  const [openHistory, setOpenHistory] = useState(false);
  const [selectedCamp, setSelectedCamp] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const { userData } = useAuth();

  const [toggleCalender, setToggleCalender] = useState(false);
  const handleOpen = () => setToggleCalender(true);
  const handleClose = () => setToggleCalender(false);
  const dispatch = useDispatch();
  const [dateRanges, setDateRanges] = useState([
    {
      startDate: addDays(new Date(), -14),
      endDate: new Date(),
      key: "selection",
    },
  ]);

  const handleDateChange = (item) => {
    // console.log(item, "item date");
    setDateRanges([
      {
        startDate: item.selection.startDate,
        endDate: item.selection.endDate,
        key: "selection",
      },
    ]);

    const dates = eachDayOfInterval({
      start: item.selection.startDate,
      end: item.selection.endDate,
    }).map((d) => dateConverter(d));

    setToggleCalender(item.selection.startDate === item.selection.endDate);
  };

  useEffect(() => {
    dispatch(
      getCampGraphResult({
        start_date: dateConverter(dateRanges[0].startDate),
        end_date: dateConverter(dateRanges[0].endDate),
      }),
    );
    dispatch(getSiteId());
  }, [dateRanges, dispatch]);

  useEffect(() => {
    dispatch(getCronLogStats({}));
  }, [dispatch]);



  useEffect(() => {
    const fetchWarnings = async () => {
      try {
        const res = await getCollectionStatsWarningsApi();
        if (res.data?.success) {
          setWarnings(res.data.data);
        }
      } catch (err) {
        console.error("Failed to fetch warnings", err);
      }
    };
    fetchWarnings();
  }, []);

  const fetchHistory = async (campId, collectionName) => {
    setSelectedCamp({ id: campId, name: collectionName });
    setOpenHistory(true);
    setHistoryLoading(true);
    try {
      const res = await getCollectionStatsHistoryApi(campId);
      if (res.data?.success) {
        setHistoryData(res.data.data);
      }
    } catch (err) {
      console.error("Failed to load history", err);
    }
    setHistoryLoading(false);
  };

  // console.log(campGraphResult, "campGraphResult");

  
  return (
    <>
      <Helmet>
        <title>Alpha Targeting || Dashboard</title>
      </Helmet>

      <AppBar
        position="static"
        sx={{
          paddingBlock: "8px",
          paddingInline: "16px",
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Stack direction={"row"} spacing={2} alignItems={"center"} sx={{}}>
          <Avatar
            sx={{
              bgcolor: indigo[200],
              width: "40px",
              height: "40px",
            }}
          >
            <Typography variant="h6">
              {userData && userData.name && userData.name[0]}
            </Typography>
          </Avatar>

          <Box>
            <Typography variant="strong" component="h6">
              {userData && userData.name}
            </Typography>
            <Typography variant="body2" component="h6">
              {userData && userData.email}
            </Typography>
          </Box>
        </Stack>

        <Typography variant="h6" component="span" textTransform="capitalize">
          {userData && userData.role}
        </Typography>
      </AppBar>
      <Paper
        elevation={3}
        sx={{ p: "8px", display: "flex", flexDirection: "column", gap: "20px" }}
      >
        <Box mt={4} width={"100%"}>
          <Grid container spacing={2}>
            {/* Total Clicks */}
            <Grid item xs={12} sm={4}>
              <Paper sx={cardStyle}>
                <Box
                  sx={{
                    ...headerStyle,
                    background: "linear-gradient(45deg,#4099ff,#73b4ff)",
                  }}
                >
                  Total Clicks
                </Box>

                <Box sx={dividerStyle} />

                <Box sx={bodyStyle}>
                  <Box sx={rowStyle}>
                    <span>Clicks</span>
                    <strong>
                      {indianNumberFormatter(
                        campGraphResult?.total_state?.sum_clicks,
                      )}
                    </strong>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            {/* Revenue */}
            <Grid item xs={12} sm={4}>
              <Paper sx={cardStyle}>
                <Box sx={{ ...headerStyle, background: "#ffb74d" }}>
                  Revenue
                </Box>

                <Box sx={dividerStyle} />

                <Box sx={bodyStyle}>
                  <Box sx={rowStyle}>
                    <span>Approved</span>
                    <strong>
                      {indianNumberFormatter(
                        campGraphResult?.total_state?.sum_approved,
                      )}
                    </strong>
                  </Box>

                  <Box sx={rowStyle}>
                    <span>Pending</span>
                    <strong>
                      {indianNumberFormatter(
                        campGraphResult?.total_state?.sum_pending,
                      )}
                    </strong>
                  </Box>

                  <Box sx={rowStyle}>
                    <span>Cancelled</span>
                    <strong>
                      {indianNumberFormatter(
                        campGraphResult?.total_state?.sum_cancelled,
                      )}
                    </strong>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            {/* Conversions */}
            <Grid item xs={12} sm={4}>
              <Paper sx={cardStyle}>
                <Box sx={{ ...headerStyle, background: "#16d316" }}>
                  Conversions
                </Box>

                <Box sx={dividerStyle} />

                <Box sx={bodyStyle}>
                  <Box sx={rowStyle}>
                    <span>Conversion</span>
                    <strong>
                      {indianNumberFormatter(
                        campGraphResult?.total_state?.sum_conversions,
                      )}
                    </strong>
                  </Box>

                  <Box sx={rowStyle}>
                    <span>Approved</span>
                    <strong>
                      {indianNumberFormatter(
                        campGraphResult?.total_state?.sum_approved_conversion,
                      )}
                    </strong>
                  </Box>

                  <Box sx={rowStyle}>
                    <span>Cancelled</span>
                    <strong>
                      {indianNumberFormatter(
                        campGraphResult?.total_state?.sum_cancelled_conversion,
                      )}
                    </strong>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
        <Stack
          direction={"row"}
          sx={{ gap: 2, justifyContent: "space-between" }}
        >
          <Button
            onClick={() => setToggleCalender(!toggleCalender)}
            startIcon={<CalendarMonth />}
            variant="contained"
            sx={{
              fontWeight: "bold",
              fontSize: "10px",
            }}
          >
            {calanderDateConverter(
              dateRanges[0].startDate,
              dateRanges[0].endDate,
            )}
          </Button>
          <Link to={"/reports/performance"}>
            <Button variant="text">View Detailed Report</Button>
          </Link>
        </Stack>

        <Box mt={2} width={"100%"}>
          <Paper sx={{ ...cardStyle, p: 2, mb: 2, borderRadius: 3, boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)" }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: "bold", color: "#1e293b", borderLeft: "5px solid #4099ff", pl: 2 }}>
                Link Statistics
              </Typography>
              <Button component={Link} to="/cron-resolver" variant="contained" size="small">
                Open Cron Resolver
              </Button>
            </Box>

            <Grid container spacing={2}>
              {/* Hourly Stats Chart */}
              <Grid item xs={12} lg={6}>
                <Paper sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: '600', color: '#64748b' }}>
                    Hourly Stats (Last 24h)
                  </Typography>
                  <Chart
                    options={{
                      chart: { type: 'bar', stacked: true, toolbar: { show: false } },
                      plotOptions: { bar: { columnWidth: '60%', borderRadius: 2 } },
                      colors: ['#FF6347', '#00BA71'],
                      xaxis: { categories: cronLogStats?.data?.hourly?.map(item => item.hour) || [], labels: { style: { fontSize: '10px' } } },
                      yaxis: { labels: { formatter: (val) => Math.floor(val) } },
                      legend: { position: 'bottom' },
                      dataLabels: { enabled: false },
                      tooltip: { shared: true, intersect: false }
                    }}
                    series={[
                      { name: 'Failed', data: cronLogStats?.data?.hourly?.map(item => Number(item.failed)) || [] },
                      { name: 'Successful', data: cronLogStats?.data?.hourly?.map(item => Number(item.successful)) || [] },
                    ]}
                    type="bar" height={220}
                  />
                </Paper>
              </Grid>

              {/* Daily Stats Chart */}
              <Grid item xs={12} lg={6}>
                <Paper sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: '600', color: '#64748b' }}>
                    Daily Stats (Last 7 Days)
                  </Typography>
                  <Chart
                    options={{
                      chart: { type: 'bar', stacked: true, toolbar: { show: false } },
                      plotOptions: { bar: { columnWidth: '50%', borderRadius: 2 } },
                      colors: ['#FF6347', '#00BA71'],
                      xaxis: { categories: cronLogStats?.data?.daily?.map(item => item.date) || [], labels: { style: { fontSize: '10px' } } },
                      yaxis: { labels: { formatter: (val) => Math.floor(val) } },
                      legend: { position: 'bottom' },
                      dataLabels: { enabled: false },
                      tooltip: { shared: true, intersect: false }
                    }}
                    series={[
                      { name: 'Failed', data: cronLogStats?.data?.daily?.map(item => Number(item.failed)) || [] },
                      { name: 'Successful', data: cronLogStats?.data?.daily?.map(item => Number(item.successful)) || [] },
                    ]}
                    type="bar" height={220}
                  />
                </Paper>
              </Grid>
            </Grid>
          </Paper>
        </Box>

        {/* Warnings Table */}
        {warnings.length > 0 && (
          <Box mt={2} width={"100%"}>
            <Paper sx={{ p: 2, borderRadius: 3, border: "1px solid #fee2e2", bgcolor: "#fef2f2" }}>
              <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: "bold", color: "#dc2626", display: "flex", alignItems: "center", gap: 1 }}>
                <span style={{ fontSize: "1.1rem" }}>⚠️</span> Tags Removed in Last 30 Days
              </Typography>
              <TableContainer component={Paper} sx={{ boxShadow: "none", border: "1px solid #fecaca" }}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: "#fee2e2" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', color: '#991b1b', fontSize: '12px' }}>Camp ID</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#991b1b', fontSize: '12px' }}>Collection Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#991b1b', fontSize: '12px' }}>Removal Date</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#991b1b', fontSize: '12px' }}>Avg Users (Before)</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#991b1b', fontSize: '12px' }}>Warning</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {warnings.map((w, index) => (
                      <TableRow 
                        key={index} 
                        sx={{ 
                          '&:last-child td, &:last-child th': { border: 0 },
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#fca5a5' } 
                        }}
                        onClick={() => fetchHistory(w.camp_id, w.collection_name)}
                      >
                        <TableCell sx={{ fontSize: '12px' }}>{w.camp_id}</TableCell>
                        <TableCell sx={{ fontSize: '12px' }}>{w.collection_name}</TableCell>
                        <TableCell sx={{ fontSize: '12px' }}>{w.last_removal_date ? new Date(w.last_removal_date).toISOString().split('T')[0] : '-'}</TableCell>
                        <TableCell sx={{ fontSize: '12px' }}>{w.avg_user_count}</TableCell>
                        <TableCell sx={{ color: '#dc2626', fontSize: '12px' }}>{w.warning_message || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>
        )}


        {/* hidden date  */}
        <Modal
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backdropFilter: "blur(2px)", // Adds blur effect
            backgroundColor: "rgba(0, 0, 0, 0.1)",
          }}
          open={toggleCalender}
          onClose={handleClose}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              backgroundColor: "white",
              borderRadius: "8px",
              overflow: "hidden",
              gap: 2,
              p: "10px",
            }}
          >
            {/* cross  */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "end",
              }}
            >
              <Tooltip>
                <ClearOutlined
                  sx={{ cursor: "pointer" }}
                  onClick={() => handleClose()}
                />
              </Tooltip>
            </Box>

            <DateRangePicker
              onChange={(item) => {
                // console.log(item, "select item");
                handleDateChange(item);
              }}
              showSelectionPreview={true}
              moveRangeOnFirstSelection={false}
              months={2}
              // shouldCloseOnSelect={false}
              direction="horizontal"
              ranges={dateRanges}
            />
          </Box>
        </Modal>

        {/* History Dialog */}
        <Dialog 
          open={openHistory} 
          onClose={() => setOpenHistory(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {selectedCamp ? `Last 30 Days History: ${selectedCamp.name} (ID: ${selectedCamp.id})` : ''}
            <IconButton onClick={() => setOpenHistory(false)}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell><strong>Server</strong></TableCell>
                    <TableCell><strong>Count</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyLoading ? (
                    <TableRow><TableCell colSpan={3} align="center">Loading...</TableCell></TableRow>
                  ) : historyData.length > 0 ? (
                    historyData.map((h, i) => (
                      <TableRow key={i}>
                        <TableCell>{moment(h.date).format('YYYY-MM-DD')}</TableCell>
                        <TableCell>{h.server_id}</TableCell>
                        <TableCell>{h.count}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow><TableCell colSpan={3} align="center">No recent history</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
        </Dialog>
      </Paper>
    </>
  );
}
export default Dashboard;
